import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  useTheme,
  alpha,
  Fade,
  Zoom,
  Skeleton,
  Tooltip
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  FilterList as FilterListIcon,
  Download as DownloadIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  LabelList
} from 'recharts';
import { format, parseJSON, parseISO } from 'date-fns';

interface SessionData {
  month: string;
  count: number;
}

interface StaffSessionData {
  staff_id: string;
  staff_name: string;
  session_count: SessionData[];
  primary_location?: string;
}

interface SessionsPerMonthChartProps {
  staffSessionData: StaffSessionData[];
  isLoading?: boolean;
}

// Custom gradient definitions
const GRADIENT_ID = 'sessionGradient';

// Enhanced tooltip component
const CustomTooltip = ({ active, payload, label }: any) => {
  const theme = useTheme();
  
  if (active && payload && payload.length) {
    const formattedDate = (() => {
      try {
        const date = parseJSON(label);
        return format(date, 'MMMM yyyy');
      } catch (e) {
        try {
          const date = parseISO(label);
          return format(date, 'MMMM yyyy');
        } catch (e) {
          return label;
        }
      }
    })();
    
    return (
      <Paper
        elevation={8}
        sx={{
          p: 2,
          bgcolor: alpha(theme.palette.background.paper, 0.95),
          backdropFilter: 'blur(10px)',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          borderRadius: 2
        }}
      >
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          {formattedDate}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              bgcolor: theme.palette.primary.main
            }}
          />
          <Typography variant="body2">
            {payload[0].value} Sessions
          </Typography>
        </Box>
      </Paper>
    );
  }
  
  return null;
};

// Format name helper
const formatName = (name: string) => {
  if (!name) return 'Unknown';
  return name.split(' ').map(part => 
    part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
  ).join(' ');
};

export const SessionsPerMonthChart: React.FC<SessionsPerMonthChartProps> = ({
  staffSessionData,
  isLoading = false
}) => {
  const theme = useTheme();
  const [selectedTab, setSelectedTab] = useState(0);
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
  const [timeFilter, setTimeFilter] = useState<'all' | '6months' | '3months'>('6months');
  
  // Calculate statistics for the selected staff member
  const selectedStaffStats = useMemo(() => {
    if (!staffSessionData[selectedTab]) return null;
    
    const allData = staffSessionData[selectedTab].session_count;
    
    // Filter out the current month for trend calculations only
    const currentDate = new Date();
    const currentMonth = format(currentDate, 'yyyy-MM');
    
    const completedMonthsData = allData.filter(item => {
      try {
        const itemMonth = format(parseJSON(item.month), 'yyyy-MM');
        return itemMonth < currentMonth;
      } catch (e) {
        try {
          const itemMonth = format(parseISO(item.month), 'yyyy-MM');
          return itemMonth < currentMonth;
        } catch (e) {
          return true; // Include if we can't parse the date
        }
      }
    });
    
    // Apply time filter to ALL data (including current month) for display stats
    const filteredData = allData.filter((_, index) => {
      if (timeFilter === 'all') return true;
      if (timeFilter === '6months') return index >= allData.length - 6;
      if (timeFilter === '3months') return index >= allData.length - 3;
      return true;
    });
    
    const total = filteredData.reduce((sum, item) => sum + item.count, 0);
    const average = filteredData.length > 0 ? total / filteredData.length : 0;
    
    // Calculate trend using ONLY completed months data
    // This ensures we always have enough data for comparison
    let trend = 0;
    if (completedMonthsData.length >= 6) {
      // We have at least 6 months of data, calculate normally
      const recentMonths = completedMonthsData.slice(-3);
      const previousMonths = completedMonthsData.slice(-6, -3);
      const recentAvg = recentMonths.reduce((sum, item) => sum + item.count, 0) / recentMonths.length;
      const previousAvg = previousMonths.reduce((sum, item) => sum + item.count, 0) / previousMonths.length;
      trend = previousAvg > 0 ? ((recentAvg - previousAvg) / previousAvg) * 100 : 0;
    } else if (completedMonthsData.length >= 3) {
      // We have 3-5 months, compare recent to overall average
      const recentMonths = completedMonthsData.slice(-3);
      const recentAvg = recentMonths.reduce((sum, item) => sum + item.count, 0) / recentMonths.length;
      const overallAvg = completedMonthsData.reduce((sum, item) => sum + item.count, 0) / completedMonthsData.length;
      trend = overallAvg > 0 ? ((recentAvg - overallAvg) / overallAvg) * 100 : 0;
    }
    // If less than 3 months, trend remains 0
    
    return {
      total,
      average,
      trend,
      highest: filteredData.length > 0 ? Math.max(...filteredData.map(item => item.count)) : 0,
      lowest: filteredData.length > 0 ? Math.min(...filteredData.map(item => item.count)) : 0
    };
  }, [staffSessionData, selectedTab, timeFilter]);
  
  // Filter data based on time selection
  const filteredChartData = useMemo(() => {
    if (!staffSessionData[selectedTab]) return [];
    
    const data = staffSessionData[selectedTab].session_count;
    
    // Include all data (including current month) for chart display
    if (timeFilter === 'all') return data;
    if (timeFilter === '6months') return data.slice(-6);
    if (timeFilter === '3months') return data.slice(-3);
    return data;
  }, [staffSessionData, selectedTab, timeFilter]);
  
  const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setFilterAnchorEl(event.currentTarget);
  };
  
  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };
  
  const handleExport = () => {
    // Export functionality could be implemented here
    console.log('Export chart data');
  };
  
  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="rectangular" height={60} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={300} />
      </Box>
    );
  }
  
  if (!staffSessionData || staffSessionData.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          No session data available
        </Typography>
      </Box>
    );
  }
  
  return (
    <Box>
      {/* Header with stats */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Staff Performance Metrics
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Filter time period">
              <IconButton size="small" onClick={handleFilterClick}>
                <FilterListIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Export data">
              <IconButton size="small" onClick={handleExport}>
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        
        {/* Stats cards */}
        {selectedStaffStats && (
          <Fade in timeout={500}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  flex: '1 1 200px',
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Total Sessions
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 600, mt: 0.5 }}>
                  {selectedStaffStats.total.toLocaleString()}
                </Typography>
              </Paper>
              
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  flex: '1 1 200px',
                  bgcolor: alpha(theme.palette.secondary.main, 0.1),
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Monthly Average
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 600, mt: 0.5 }}>
                  {selectedStaffStats.average.toFixed(1)}
                </Typography>
              </Paper>
              
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  flex: '1 1 200px',
                  bgcolor: alpha(
                    selectedStaffStats.trend >= 0 ? theme.palette.success.main : theme.palette.error.main,
                    0.1
                  ),
                  borderRadius: 2,
                  border: `1px solid ${alpha(
                    selectedStaffStats.trend >= 0 ? theme.palette.success.main : theme.palette.error.main,
                    0.2
                  )}`
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    3-Month Trend
                  </Typography>
                  {selectedStaffStats.trend >= 0 ? (
                    <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main' }} />
                  ) : (
                    <TrendingDownIcon sx={{ fontSize: 16, color: 'error.main' }} />
                  )}
                </Box>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 600, 
                    mt: 0.5,
                    color: selectedStaffStats.trend >= 0 ? 'success.main' : 'error.main'
                  }}
                >
                  {selectedStaffStats.trend >= 0 ? '+' : ''}{selectedStaffStats.trend.toFixed(1)}%
                </Typography>
              </Paper>
            </Box>
          </Fade>
        )}
      </Box>
      
      {/* Tabs for staff selection */}
      {staffSessionData.length > 1 && (
        <Tabs
          value={selectedTab}
          onChange={(_, newValue) => setSelectedTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            mb: 3,
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              minHeight: 48,
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 500,
              '&.Mui-selected': {
                fontWeight: 600
              }
            }
          }}
        >
          {staffSessionData.map((staff, index) => (
            <Tab
              key={staff.staff_id}
              label={formatName(staff.staff_name)}
            />
          ))}
        </Tabs>
      )}
      
      {/* Chart */}
      <Zoom in timeout={600}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            bgcolor: alpha(theme.palette.background.paper, 0.8),
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`,
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          {/* Background decoration */}
          <Box
            sx={{
              position: 'absolute',
              top: -100,
              right: -100,
              width: 300,
              height: 300,
              borderRadius: '50%',
              background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.05)} 0%, transparent 70%)`,
              pointerEvents: 'none'
            }}
          />
          
          <ResponsiveContainer width="100%" height={400}>
            <BarChart
              data={filteredChartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
            >
              <defs>
                <linearGradient id={GRADIENT_ID} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={theme.palette.primary.main} stopOpacity={1} />
                  <stop offset="100%" stopColor={theme.palette.primary.dark} stopOpacity={0.8} />
                </linearGradient>
              </defs>
              
              <CartesianGrid 
                strokeDasharray="3 3" 
                stroke={alpha(theme.palette.divider, 0.3)}
                vertical={false}
              />
              
              <XAxis
                dataKey="month"
                tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                angle={-45}
                textAnchor="end"
                height={80}
                tickFormatter={(value) => {
                  try {
                    const date = parseJSON(value);
                    return format(date, 'MMM yy');
                  } catch (e) {
                    try {
                      const date = parseISO(value);
                      return format(date, 'MMM yy');
                    } catch (e) {
                      return value;
                    }
                  }
                }}
              />
              
              <YAxis
                tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                label={{
                  value: 'Number of Sessions',
                  angle: -90,
                  position: 'insideLeft',
                  style: { 
                    textAnchor: 'middle',
                    fill: theme.palette.text.secondary,
                    fontSize: 14
                  }
                }}
              />
              
              <RechartsTooltip
                content={<CustomTooltip />}
                cursor={{ fill: alpha(theme.palette.primary.main, 0.1) }}
              />
              
              <Bar
                dataKey="count"
                fill={`url(#${GRADIENT_ID})`}
                radius={[8, 8, 0, 0]}
                animationDuration={1000}
                animationBegin={0}
              >
                <LabelList
                  dataKey="count"
                  position="top"
                  style={{ fill: theme.palette.text.secondary, fontSize: 11 }}
                />
                {filteredChartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={
                      entry.count === selectedStaffStats?.highest
                        ? theme.palette.success.main
                        : entry.count === selectedStaffStats?.lowest
                        ? theme.palette.warning.main
                        : `url(#${GRADIENT_ID})`
                    }
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      </Zoom>
      
      {/* Filter menu */}
      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={handleFilterClose}
        PaperProps={{
          elevation: 8,
          sx: {
            mt: 1,
            minWidth: 150
          }
        }}
      >
        <MenuItem
          selected={timeFilter === '3months'}
          onClick={() => {
            setTimeFilter('3months');
            handleFilterClose();
          }}
        >
          Last 3 Months
        </MenuItem>
        <MenuItem
          selected={timeFilter === '6months'}
          onClick={() => {
            setTimeFilter('6months');
            handleFilterClose();
          }}
        >
          Last 6 Months
        </MenuItem>
        <MenuItem
          selected={timeFilter === 'all'}
          onClick={() => {
            setTimeFilter('all');
            handleFilterClose();
          }}
        >
          All Time
        </MenuItem>
      </Menu>
    </Box>
  );
};